import {SharedGraph} from '../../navigation/SharedGraph';
import {readSharedReport, useSharedReport} from '../../navigation/useSharedReport';
// src/shared/ui/chart/LineChartWithToggles.jsx

import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useReportPreference } from './useReportPreference';
import { ChartSummary } from './ChartSummary';
import { Line, Bar } from "react-chartjs-2";

import {
  CHART_TYPE_OPTIONS,
  GROUP_TITLES,
  titleCaseGroup,
  stripGroupPrefix,
  getInitialVisibleKeys,
  buildAutoGroupedSeries,
  buildManualGroupedToggles,
  buildProvidedTotalSeries,
  buildTotalBudgetSeries,
  getNoneSelectedTotalSeries,
  computeScaleMode,
  buildLineChartData,
  buildLineChartOptions,
} from "./charts";

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  BarElement,
  Filler,
} from "chart.js";

/**
 * Register only the Chart.js primitives we actually use.
 * This keeps bundle size under control versus registering everything.
 */
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  BarElement,
  Filler,
);

/**
 * @typedef {"currency"|"count"|"percent"|"text"} ValueType
 */

/**
 * @typedef {Object} ChartSeries
 * @property {string} key
 * @property {string} label
 * @property {(number[]|null[])} data
 * @property {(number[]|null[])} [proposedData]
 * @property {string} [group]
 * @property {boolean} [defaultVisible]
 * @property {ValueType} [valueType="currency"]
 * @property {string} [color]
 */

/**
 * @typedef {Object} SeriesToggleGroup
 * @property {string} key
 * @property {string} label
 * @property {string} [groupKey="other"]
 * @property {string[]} seriesKeys
 * @property {boolean} [defaultVisible]
 */

/**
 * @typedef {"overlay"|"merge"} ProposedRenderMode
 * @typedef {"auto"|"zero"} YAxisBaselineMode
 * @typedef {"checkbox"|"search"} ToggleUIMode
 */

function isNonEmptyString(s) {
  return typeof s === "string" && s.trim().length > 0;
}

function makeRowId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function normalizeText(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase();
}

function extractLeadingInt(s) {
  const m = String(s ?? "").match(/(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

const NATURAL_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

function naturalCompare(a, b) {
  return NATURAL_COLLATOR.compare(String(a ?? ""), String(b ?? ""));
}

function startsWithNumberLike(s) {
  // "15 dice", "2024", "3.5 something" => true
  // "FY 2024" => false (doesn't start with number)
  return /^\s*\d+(\.\d+)?\b/.test(String(s ?? ""));
}

function areStringArraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function getToggleGroupDomKey(prefix, groupKey) {
  return `${prefix}-${String(groupKey || "other")
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, "-")}`;
}

export function LineChartWithToggles(props) {
  return <SharedGraph title={props.title} shareId={props.shareId}>{id => <SharedLineChart key={id} {...props} shareId={id}/>}</SharedGraph>;
}
function SharedLineChart({
  labels,
  series,
  title,
  shareId,
  shareHeadingId,
  defaultVisibleKeys,
  groups,
  showTotalWhenNoneSelected = false,
  totalLabel = "Total Budget",
  showSeriesToggles = true,
  showChartTypeSelector = true,
  proposedRenderMode = "overlay",
  defaultYAxisMode = "zero",

  // axis title overrides
  xAxisTitle,
  showAllYearLabels = false,
  yAxisTitle,

  // formatting overrides
  yAxisTickFormatter, // (value:number) => string
  tooltipValueFormatter, // (value:number) => string
  showLegend = true,
  showChartSummary = false,

  // Search UI controls
  toggleUIMode,
  searchToggleThreshold = 100,
  searchPlaceholder = "Search…",
  searchMaxRows = 6,
  onVisibleKeysChange,

// Generic x-axis trimming
xTrimMode = "none", // "none" | "visibleData"
xTrimPadding = 0,   // keep a few extra points if you want

  // turn on while debugging
  debugSearch = false,

  initialChartType = "line",
  spanGaps = false,
}) {
  const supportedInitialChartTypes = new Set([
    "line",
    "bar",
    "stackedBar",
    "stackedBar100",
    "area",
    "stackedArea",
  ]);
  const usingGroups = Array.isArray(groups) && groups.length > 0;
  const safeSeries = Array.isArray(series) ? series : [];

  const [shared] = useState(() => readSharedReport(shareId));
  const preferenceKey = `${title || 'chart'}:${safeSeries.map(item => item.key).join('|')}`;
  const [chartType, setChartType] = useReportPreference(`${preferenceKey}:type`,
    supportedInitialChartTypes.has(initialChartType)
      ? initialChartType
      : "line",
    supportedInitialChartTypes.has(shared.type) ? shared.type : undefined,
  );
  const [yAxisMode, setYAxisMode] = useReportPreference(`${preferenceKey}:axis`,
    defaultYAxisMode === "zero" ? "zero" : "auto",
    ["zero", "auto"].includes(shared.axis) ? shared.axis : undefined,
  );

  const [hasUserToggled, setHasUserToggled] = useReportPreference(`${preferenceKey}:toggled`, false, Array.isArray(shared.series) ? true : undefined);
  const [activeRowId, setActiveRowId] = useState(null);
  const [openToggleGroupKey, setOpenToggleGroupKey] = useState(null);

  const [visibleKeys, setVisibleKeys] = useReportPreference(`${preferenceKey}:visible`, () => {
    if (!showSeriesToggles) return safeSeries.map((s) => s.key);
    if (showTotalWhenNoneSelected) return [];
    return getInitialVisibleKeys(safeSeries, defaultVisibleKeys, groups);
  }, Array.isArray(shared.series) ? shared.series.filter(key => safeSeries.some(item => item.key === key)) : undefined);
  const activateShare = useSharedReport(shareId, shareHeadingId, {type: chartType, axis: yAxisMode, series: visibleKeys});

  useEffect(() => {
    if (typeof onVisibleKeysChange === "function") {
      onVisibleKeysChange(visibleKeys);
    }
  }, [visibleKeys, onVisibleKeysChange]);

  useEffect(() => {
    const allKeys = safeSeries.map((s) => s.key);

    if (hasUserToggled) return;

    if (!showSeriesToggles) {
      setVisibleKeys((prev) =>
        areStringArraysEqual(prev, allKeys) ? prev : allKeys
      );
      return;
    }

    if (showTotalWhenNoneSelected) {
      setVisibleKeys((prev) =>
        areStringArraysEqual(prev, []) ? prev : []
      );
      return;
    }

    const nextKeys = getInitialVisibleKeys(
      safeSeries,
      defaultVisibleKeys,
      groups,
    );

    setVisibleKeys((prev) =>
      areStringArraysEqual(prev, nextKeys) ? prev : nextKeys
    );
  }, [
    safeSeries,
    defaultVisibleKeys,
    groups,
    showTotalWhenNoneSelected,
    hasUserToggled,
    showSeriesToggles,
  ]);

  const toggleKey = (key) => {
    const seriesObj = seriesByKey.get(key);
    if (seriesObj?.disabled) return;

    setHasUserToggled(true);
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleGroup = (group) => {
    if (group?.disabled) return;
    if (!group || !Array.isArray(group.seriesKeys)) return;

    setHasUserToggled(true);
    setVisibleKeys((prev) => {
      const set = new Set(prev);
      const enabledKeys = group.seriesKeys.filter((k) => {
        const seriesObj = seriesByKey.get(k);
        return !seriesObj?.disabled;
      });
      const allVisible =
        enabledKeys.length > 0 && enabledKeys.every((k) => set.has(k));

      if (allVisible) enabledKeys.forEach((k) => set.delete(k));
      else enabledKeys.forEach((k) => set.add(k));

      return Array.from(set);
    });
  };

  const groupedSeries = useMemo(
    () => buildAutoGroupedSeries(safeSeries, usingGroups),
    [safeSeries, usingGroups],
  );

    const shouldUseSearchUI = useMemo(() => {
    if (!showSeriesToggles) return false;
    if (usingGroups) return false;
    if (toggleUIMode === "search") return true;
    if (toggleUIMode === "checkbox") return false;
    return safeSeries.length >= searchToggleThreshold;
  }, [
    showSeriesToggles,
    usingGroups,
    toggleUIMode,
    safeSeries.length,
    searchToggleThreshold,
  ]);


  const shouldNaturalSortCheckboxes = useMemo(() => {
    // Only affects the checkbox UI (not the search UI)
    if (!showSeriesToggles) return false;
    if (usingGroups) return false;
    if (shouldUseSearchUI) return false;

    const displayLabels = safeSeries.map((s) =>
      stripGroupPrefix(String(s?.label ?? s?.key ?? ""), s?.group),
    );

    if (displayLabels.length < 3) return false;

    const numberFirstCount = displayLabels.reduce((acc, lbl) => {
      return acc + (startsWithNumberLike(lbl) ? 1 : 0);
    }, 0);

    // Only flip on when MOST labels are "number-first" (dice chart case)
    return numberFirstCount / displayLabels.length >= 0.6;
  }, [showSeriesToggles, usingGroups, shouldUseSearchUI, safeSeries]);

  const groupedSeriesForCheckboxes = useMemo(() => {
    if (!shouldNaturalSortCheckboxes) return groupedSeries;

    return (groupedSeries || []).map(([groupKey, list]) => {
      const nextList = Array.isArray(list) ? [...list] : [];

      nextList.sort((a, b) => {
        const la = stripGroupPrefix(String(a?.label ?? a?.key ?? ""), a?.group);
        const lb = stripGroupPrefix(String(b?.label ?? b?.key ?? ""), b?.group);
        return naturalCompare(la, lb);
      });

      return [groupKey, nextList];
    });
  }, [groupedSeries, shouldNaturalSortCheckboxes]);

  const manualGroupedToggles = useMemo(
    () => buildManualGroupedToggles(groups, usingGroups),
    [groups, usingGroups],
  );

  const providedTotalSeries = useMemo(
    () => buildProvidedTotalSeries(safeSeries, usingGroups, totalLabel),
    [safeSeries, usingGroups, totalLabel],
  );

  const totalBudgetSeries = useMemo(
    () => buildTotalBudgetSeries(labels, safeSeries, totalLabel),
    [labels, safeSeries, totalLabel],
  );

  const noneSelectedTotalSeries = useMemo(
    () => getNoneSelectedTotalSeries(providedTotalSeries, totalBudgetSeries),
    [providedTotalSeries, totalBudgetSeries],
  );

  const scaleMode = useMemo(
    () =>
      computeScaleMode({
        safeSeries,
        visibleKeys,
        showTotalWhenNoneSelected,
        noneSelectedTotalSeries,
      }),
    [
      safeSeries,
      visibleKeys,
      showTotalWhenNoneSelected,
      noneSelectedTotalSeries,
    ],
  );

  const chartData = useMemo(
    () =>
      buildLineChartData({
        labels,
        safeSeries,
        visibleKeys,
        chartType,
        showTotalWhenNoneSelected,
        noneSelectedTotalSeries,
        totalLabel,
        usingGroups,
        proposedRenderMode,
        spanGaps,
      }),
    [
      labels,
      safeSeries,
      visibleKeys,
      chartType,
      showTotalWhenNoneSelected,
      noneSelectedTotalSeries,
      totalLabel,
      usingGroups,
      proposedRenderMode,
      spanGaps,
    ],
  );

  const yRange = useMemo(() => {
    const ds = Array.isArray(chartData?.datasets) ? chartData.datasets : [];
    const values = [];

    for (const d of ds) {
      const arr = Array.isArray(d?.data) ? d.data : [];
      for (const v of arr) {
        if (typeof v === "number" && Number.isFinite(v)) values.push(v);
      }
    }

    if (!values.length) return null;

    let min = values[0];
    let max = values[0];

    for (const v of values) {
      if (v < min) min = v;
      if (v > max) max = v;
    }

    if (min === max) {
      const bump = Math.max(1, Math.abs(min) * 0.02);
      return { min: min - bump, max: max + bump };
    }

    const span = max - min;
    const pad = span * 0.06;
    return { min: min - pad, max: max + pad };
  }, [chartData]);

  const options = useMemo(() => {
    const base = buildLineChartOptions({ scaleMode, chartType }) || {};
    const next = { ...base };

    // ensure nested objects exist
    next.plugins = next.plugins ? { ...next.plugins } : {};
    next.plugins.tooltip = next.plugins.tooltip
      ? { ...next.plugins.tooltip }
      : {};
    next.plugins.tooltip.callbacks = next.plugins.tooltip.callbacks
      ? { ...next.plugins.tooltip.callbacks }
      : {};
    next.plugins.legend = next.plugins.legend ? { ...next.plugins.legend } : {};
    next.plugins.legend.display = showLegend;

    next.scales = next.scales ? { ...next.scales } : {};
    if (showAllYearLabels) {
      next.scales.x = { ...next.scales.x, ticks: { ...next.scales.x?.ticks, autoSkip: false, minRotation: 60, maxRotation: 60 } };
    }
    const baseX = next.scales.x ? { ...next.scales.x } : {};
    const baseY = next.scales.y ? { ...next.scales.y } : {};

    // ---------------- existing logic: stacked 100 ----------------
    if (chartType === "stackedBar100") {
      const ds = Array.isArray(chartData?.datasets) ? chartData.datasets : [];
      let hasNegative = false;

      for (const d of ds) {
        if (d?._isHelper) continue;
        const arr = Array.isArray(d?.data) ? d.data : [];
        for (const v of arr) {
          if (typeof v === "number" && Number.isFinite(v) && v < 0) {
            hasNegative = true;
            break;
          }
        }
        if (hasNegative) break;
      }

      baseY.beginAtZero = true;
      delete baseY.suggestedMin;
      delete baseY.suggestedMax;

      baseY.min = hasNegative ? -100 : 0;
      baseY.max = 100;

      // ✅ Apply axis title overrides (optional)
      if (typeof xAxisTitle === "string" && xAxisTitle.trim()) {
        baseX.title = {
          ...(baseX.title || {}),
          display: true,
          text: xAxisTitle,
        };
      }
      if (typeof yAxisTitle === "string" && yAxisTitle.trim()) {
        baseY.title = {
          ...(baseY.title || {}),
          display: true,
          text: yAxisTitle,
        };
      }

      // ✅ Apply formatting overrides (optional)
      if (typeof yAxisTickFormatter === "function") {
        baseY.ticks = {
          ...(baseY.ticks || {}),
          callback: (v) => yAxisTickFormatter(v),
        };
      }
      if (typeof tooltipValueFormatter === "function") {
        next.plugins.tooltip.callbacks.label = (ctx) => {
          const raw = ctx?.raw;
          if (typeof raw === "number" && Number.isFinite(raw))
            return tooltipValueFormatter(raw);
          return String(ctx?.formattedValue ?? "");
        };
      }

      next.scales.x = baseX;
      next.scales.y = baseY;
      return next;
    }

    // ---------------- existing logic: yAxisMode + yRange -------------
    if (yAxisMode === "zero") {
      baseY.beginAtZero = true;
      delete baseY.min;
      delete baseY.max;
      delete baseY.suggestedMin;
      delete baseY.suggestedMax;
    } else {
      baseY.beginAtZero = false;

      if (yRange) {
        baseY.suggestedMin = yRange.min;
        baseY.suggestedMax = yRange.max;
      } else {
        delete baseY.suggestedMin;
        delete baseY.suggestedMax;
      }

      delete baseY.min;
      delete baseY.max;
    }

    // ✅ Apply axis title overrides (optional)
    if (typeof xAxisTitle === "string" && xAxisTitle.trim()) {
      baseX.title = { ...(baseX.title || {}), display: true, text: xAxisTitle };
    }
    if (typeof yAxisTitle === "string" && yAxisTitle.trim()) {
      baseY.title = { ...(baseY.title || {}), display: true, text: yAxisTitle };
    }

    // ✅ Apply formatting overrides (optional)
    if (typeof yAxisTickFormatter === "function") {
      baseY.ticks = {
        ...(baseY.ticks || {}),
        callback: (v) => yAxisTickFormatter(v),
      };
    }
    if (typeof tooltipValueFormatter === "function") {
      next.plugins.tooltip.callbacks.label = (ctx) => {
        const raw = ctx?.raw;
        if (typeof raw === "number" && Number.isFinite(raw))
          return tooltipValueFormatter(raw);
        return String(ctx?.formattedValue ?? "");
      };
    }

    next.scales.x = baseX;
    next.scales.y = baseY;
    return next;
  }, [
    scaleMode,
    chartType,
    yAxisMode,
    yRange,
    chartData,
    xAxisTitle,
    showAllYearLabels,
    yAxisTitle,
    yAxisTickFormatter,
    tooltipValueFormatter,
    showLegend,
  ]);

const trimmedChartData = useMemo(() => {
  if (xTrimMode !== "visibleData") return chartData;

  const labelsArr = Array.isArray(chartData?.labels) ? chartData.labels : [];
  const dsArr = Array.isArray(chartData?.datasets) ? chartData.datasets : [];
  if (!labelsArr.length || !dsArr.length) return chartData;

  // Which datasets are actually visible in this instance?
  const visibleSet = new Set(Array.isArray(visibleKeys) ? visibleKeys : []);

  // Find the first and last index where any visible dataset has a finite number.
  let first = labelsArr.length;
  let last = -1;

  for (const d of dsArr) {
    // buildLineChartData should set dataset.key (or some identifier).
    // If it doesn't today, we should add it there (see note below).
    const datasetKey = d?.key || d?._seriesKey || d?.label;
    if (showSeriesToggles && datasetKey && !visibleSet.has(datasetKey)) continue;

    const arr = Array.isArray(d?.data) ? d.data : [];
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (typeof v === "number" && Number.isFinite(v)) {
        if (i < first) first = i;
        break;
      }
    }
    for (let i = arr.length - 1; i >= 0; i--) {
      const v = arr[i];
      if (typeof v === "number" && Number.isFinite(v)) {
        if (i > last) last = i;
        break;
      }
    }
  }

  if (first >= labelsArr.length || last < 0) return chartData;

  const start = Math.max(0, first - Math.max(0, xTrimPadding));
  const end = Math.min(labelsArr.length - 1, last + Math.max(0, xTrimPadding));
  if (start === 0 && end >= labelsArr.length - 1) return chartData;

  return {
    ...chartData,
    labels: labelsArr.slice(start, end + 1),
    datasets: dsArr.map((d) => {
      const next = { ...d };
      if (Array.isArray(next.data)) next.data = next.data.slice(start, end + 1);
      // If you ever add proposedData to datasets, trim it too
      if (Array.isArray(next.proposedData))
        next.proposedData = next.proposedData.slice(start, end + 1);
      return next;
    }),
  };
}, [xTrimMode, xTrimPadding, chartData, visibleKeys, showSeriesToggles]);

  const seriesByKey = useMemo(() => {
    const m = new Map();
    for (const s of safeSeries) m.set(s.key, s);
    return m;
  }, [safeSeries]);

  const seriesByNormalizedLabel = useMemo(() => {
    const m = new Map();
    for (const s of safeSeries) {
      const k = normalizeText((s?.label ?? s?.key) || "");
      if (!k) continue;
      if (!m.has(k)) m.set(k, s);
    }
    return m;
  }, [safeSeries]);

  const [searchRows, setSearchRows] = useState(() => {
    const keys = Array.isArray(visibleKeys) ? visibleKeys : [];
    if (!keys.length) return [{ id: makeRowId(), text: "", selectedKey: null }];

    const rows = keys
      .map((k) => {
        const s = seriesByKey.get(k);
        return {
          id: makeRowId(),
          text: String(s?.label ?? ""),
          selectedKey: s ? s.key : k,
        };
      })
      .slice(0, Math.max(1, searchMaxRows));

    return rows.length
      ? rows
      : [{ id: makeRowId(), text: "", selectedKey: null }];
  });

  useEffect(() => {
    if (!shouldUseSearchUI) return;

    if (hasUserToggled) {
      setSearchRows((prev) => {
        const next = (prev || []).filter((r) => {
          if (!r?.selectedKey) return true;
          return seriesByKey.has(r.selectedKey);
        });
        return next.length
          ? next
          : [{ id: makeRowId(), text: "", selectedKey: null }];
      });
      return;
    }

    setSearchRows(() => {
      const keys = Array.isArray(visibleKeys) ? visibleKeys : [];
      if (!keys.length)
        return [{ id: makeRowId(), text: "", selectedKey: null }];

      const rows = keys
        .map((k) => {
          const s = seriesByKey.get(k);
          return {
            id: makeRowId(),
            text: String(s?.label ?? ""),
            selectedKey: s ? s.key : k,
          };
        })
        .slice(0, Math.max(1, searchMaxRows));

      return rows.length
        ? rows
        : [{ id: makeRowId(), text: "", selectedKey: null }];
    });
  }, [
    shouldUseSearchUI,
    hasUserToggled,
    visibleKeys,
    seriesByKey,
    searchMaxRows,
  ]);

  useEffect(() => {
    if (!shouldUseSearchUI) return;

    setVisibleKeys((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const desired = searchRows
        .map((r) => r?.selectedKey)
        .filter((k) => isNonEmptyString(k) && seriesByKey.has(k));

      const out = [];
      const seen = new Set();
      for (const k of desired) {
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(k);
      }

      if (
        prevArr.length === out.length &&
        prevArr.every((k, i) => k === out[i])
      ) {
        return prevArr;
      }
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseSearchUI, searchRows, seriesByKey]);

  const updateRowText = useCallback((rowId, nextText) => {
    setHasUserToggled(true);
    setSearchRows((prev) =>
      (prev || []).map((r) =>
        r.id === rowId ? { ...r, text: nextText, selectedKey: null } : r,
      ),
    );
  }, []);

  const setRowSelection = useCallback((rowId, seriesObj) => {
    if (!seriesObj || !seriesObj.key) return;

    setHasUserToggled(true);
    setSearchRows((prev) =>
      (prev || []).map((r) =>
        r.id === rowId
          ? {
              ...r,
              text: String(seriesObj.label ?? ""),
              selectedKey: seriesObj.key,
            }
          : r,
      ),
    );
  }, []);

  const addSearchRow = useCallback(() => {
    setHasUserToggled(true);
    setSearchRows((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      if (list.length >= searchMaxRows) return list;
      return [...list, { id: makeRowId(), text: "", selectedKey: null }];
    });
  }, [searchMaxRows]);

  const removeSearchRow = useCallback((rowId) => {
    setHasUserToggled(true);
    setSearchRows((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const next = list.filter((r) => r.id !== rowId);
      return next.length
        ? next
        : [{ id: makeRowId(), text: "", selectedKey: null }];
    });
  }, []);

  // normalizeText can be too weak if labels have double-spaces or group prefixes
  const normalizeLabelForSearch = useCallback((s) => {
    const rawLabel = String(s?.label ?? s?.key ?? "");
    const noPrefix =
      typeof stripGroupPrefix === "function"
        ? stripGroupPrefix(rawLabel, s?.group)
        : rawLabel;

    return normalizeText(noPrefix.replace(/\s+/g, " "));
  }, []);

  const getSuggestionsForRow = useCallback(
    (row) => {
      const q = normalizeText(String(row?.text ?? "").replace(/\s+/g, " "));
      if (!q) return [];

      const selectedKeys = new Set(
        (searchRows || [])
          .map((r) => r?.selectedKey)
          .filter((k) => isNonEmptyString(k)),
      );
      if (row?.selectedKey) selectedKeys.delete(row.selectedKey);

      const matches = safeSeries.filter((s) => {
        if (!s?.key) return false;
        if (selectedKeys.has(s.key)) return false;

        const label = normalizeLabelForSearch(s);
        if (!label) return false;

        return label.includes(q);
      });

      matches.sort((a, b) => {
        const la = normalizeLabelForSearch(a);
        const lb = normalizeLabelForSearch(b);
        const aStarts = la.startsWith(q) ? 0 : 1;
        const bStarts = lb.startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return la.localeCompare(lb);
      });

      const out = matches.slice(0, 12);

      if (debugSearch) {
        // only log when actively typing in a focused row
        if (activeRowId === row?.id) {
          console.log("[LineChartWithToggles][search]", {
            q,
            safeSeriesCount: safeSeries.length,
            matchCount: out.length,
            sampleLabels: safeSeries.slice(0, 8).map((s) => s?.label ?? s?.key),
          });
        }
      }

      return out;
    },
    [safeSeries, searchRows, normalizeLabelForSearch, debugSearch, activeRowId],
  );

  const commitRowByEnter = useCallback(
    (row) => {
      const q = normalizeText(row?.text);
      if (!q) return;

      const exact = seriesByNormalizedLabel.get(q);
      if (exact) {
        setRowSelection(row.id, exact);
        return;
      }

      const sugg = getSuggestionsForRow(row);
      if (sugg.length > 0) setRowSelection(row.id, sugg[0]);
    },
    [getSuggestionsForRow, seriesByNormalizedLabel, setRowSelection],
  );

  return (
    <div onChange={activateShare} onClick={activateShare} className="mt-3 border border-gray-300 rounded bg-white">
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-center justify-between gap-3">
        {title ? <h3 className="text-lg font-semibold">{title}</h3> : <div />}

        <div className="flex flex-wrap items-center gap-3 text-sm">
          {showChartTypeSelector && (
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Chart type:</span>
              <select
                aria-label={title ? `${title} chart type` : 'Chart type'}
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 bg-white text-xs"
              >
                {CHART_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-gray-600">Y-axis:</span>
            <select
              role="listbox"
              aria-label="Y-axis baseline"
              value={yAxisMode}
              onChange={(e) =>
                setYAxisMode(e.target.value === "zero" ? "zero" : "auto")
              }
              className="border border-gray-300 rounded px-2 py-1 bg-white text-xs"
              title="Auto fits the y-axis to the visible data; Zero-based forces the axis to start at 0."
            >
              <option value="auto">Auto (fit data)</option>
              <option value="zero">Zero-based</option>
            </select>
          </div>
        </div>
      </div>

      {showSeriesToggles && (
        // ✅ critical: ensure the whole search/toggle region is ABOVE the chart canvas
        <div
          className={`px-4 ${shouldUseSearchUI ? "pt-0" : "pt-1"} relative z-20`}
        >
          {shouldUseSearchUI ? (
            <div className="pb-2 -mt-1">
              <div className="space-y-2">
                {searchRows.map((row, idx) => {
                  const qNorm = normalizeText(row?.text);
                  const suggestions = getSuggestionsForRow(row);

                  const showDropdown = activeRowId === row.id && qNorm;

                  const isLast = idx === searchRows.length - 1;
                  const canAddMore = searchRows.length < searchMaxRows;
                  const canRemove = searchRows.length > 1;

                  return (
                    <div
                      key={row.id}
                      className="flex items-center gap-2 text-xs justify-start"
                    >
                      <div className="relative w-full max-w-[560px] min-w-[260px]">
                        <input
                          className="w-full h-9 border border-gray-300 rounded px-2 bg-white text-xs"
                          value={row.text}
                          onFocus={() => setActiveRowId(row.id)}
                          onBlur={() =>
                            window.setTimeout(() => setActiveRowId(null), 120)
                          }
                          onChange={(e) =>
                            updateRowText(row.id, e.target.value)
                          }
                          placeholder={searchPlaceholder}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitRowByEnter(row);
                            }
                          }}
                        />

                        {/* ✅ small inline debug line so you can tell if it's data vs CSS */}
                        {debugSearch && activeRowId === row.id && qNorm ? (
                          <div className="mt-1 text-[11px] text-gray-500">
                            matches: {suggestions.length} / series:{" "}
                            {safeSeries.length}
                          </div>
                        ) : null}

                        {showDropdown && (
                          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow-sm max-h-56 overflow-auto text-gray-900">
                            {suggestions.length > 0 ? (
                              suggestions.map((s) => (
                                <button
                                  key={s.key}
                                  type="button"
                                  // tighter rows: less padding + tighter leading
                                  className="w-full text-left px-2 py-[3px] text-[11px] leading-4 text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setRowSelection(row.id, s);
                                    setActiveRowId(null);
                                  }}
                                  title={s.label}
                                >
                                  {s.label}
                                </button>
                              ))
                            ) : (
                              <div className="px-2 py-2 text-xs text-gray-600">
                                No matches
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        className={`w-9 h-9 rounded border text-xs font-semibold ${
                          canRemove
                            ? "border-red-300 bg-red-50 hover:bg-red-100 text-red-700"
                            : "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed"
                        }`}
                        onClick={() => {
                          if (!canRemove) return;
                          removeSearchRow(row.id);
                        }}
                        title={
                          canRemove ? "Remove" : "At least one row is required"
                        }
                        disabled={!canRemove}
                      >
                        ✕
                      </button>

                      {isLast ? (
                        <button
                          type="button"
                          className={`w-9 h-9 rounded border text-xs font-semibold ${
                            canAddMore
                              ? "border-green-300 bg-green-50 hover:bg-green-100 text-green-700"
                              : "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed"
                          }`}
                          onClick={() => {
                            if (!canAddMore) return;
                            addSearchRow();
                          }}
                          title={
                            canAddMore
                              ? "Add another"
                              : `Max ${searchMaxRows} selections`
                          }
                          disabled={!canAddMore}
                        >
                          +
                        </button>
                      ) : (
                        <div className="w-9 h-9" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-start gap-2 text-sm mb-3">
              {toggleUIMode === "inline"
                ? safeSeries.map((s) => {
                    const displayLabel = stripGroupPrefix(s.label, s.group);
                    const disabled = !!s.disabled;
                    return (
                      <label
                        key={s.key}
                        className={`flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm ${
                          disabled
                            ? "cursor-not-allowed text-gray-400"
                            : "cursor-pointer text-gray-900 hover:bg-gray-50"
                        }`}
                        title={s.label}
                      >
                        <input
                          type="checkbox"
                          checked={visibleKeys.includes(s.key)}
                          disabled={disabled}
                          onChange={() => toggleKey(s.key)}
                          className="h-4 w-4 rounded border-gray-400"
                        />
                        <span>{displayLabel}</span>
                      </label>
                    );
                  })
                : usingGroups
                ? manualGroupedToggles.map(([groupKey, list]) => {
                    const groupTitle =
                      GROUP_TITLES[groupKey] || titleCaseGroup(groupKey);
                    const domKey = getToggleGroupDomKey("manual", groupKey);
                    const panelId = `${domKey}-chart-toggle-panel`;
                    const selectedCount = (list || []).filter(
                      (g) =>
                        Array.isArray(g.seriesKeys) &&
                        g.seriesKeys.length > 0 &&
                        g.seriesKeys.every((k) => visibleKeys.includes(k)),
                    ).length;
                    const totalCount = Array.isArray(list) ? list.length : 0;
                    const isOpen = openToggleGroupKey === domKey;

                    return (
                      <div key={groupKey} className="relative">
                        <button
                          type="button"
                          className="min-w-[180px] rounded border border-gray-300 bg-white px-4 py-3 text-left shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() =>
                            setOpenToggleGroupKey((prev) =>
                              prev === domKey ? null : domKey,
                            )
                          }
                        >
                          <span className="flex items-center justify-between gap-4">
                            <span>
                              <span className="block text-sm font-semibold text-gray-900">
                                {groupTitle}
                              </span>
                              <span className="block text-xs text-gray-600">
                                {selectedCount} of {totalCount} selected
                              </span>
                            </span>
                            <span
                              aria-hidden="true"
                              className="text-lg font-semibold text-gray-700"
                            >
                              {isOpen ? "^" : "v"}
                            </span>
                          </span>
                        </button>

                        {isOpen && (
                          <div
                            id={panelId}
                            className="absolute left-0 top-full z-50 mt-2 w-[min(360px,calc(100vw-2rem))] rounded border border-gray-300 bg-white p-2 shadow-lg"
                          >
                          {list.map((g) => {
                            const allVisible =
                              Array.isArray(g.seriesKeys) &&
                              g.seriesKeys.length > 0 &&
                              g.seriesKeys.every((k) =>
                                visibleKeys.includes(k),
                              );
                            const disabled = !!g.disabled;

                            return (
                              <label
                                key={g.key}
                                className={`flex items-start gap-3 rounded px-3 py-3 text-sm ${
                                  disabled
                                    ? "cursor-not-allowed text-gray-400"
                                    : "cursor-pointer text-gray-900 hover:bg-gray-50"
                                }`}
                                title={g.label}
                              >
                                <input
                                  type="checkbox"
                                  checked={allVisible}
                                  disabled={disabled}
                                  onChange={() => toggleGroup(g)}
                                  className="mt-0.5 h-5 w-5 rounded border-gray-400"
                                />
                                <span className="leading-5">{g.label}</span>
                              </label>
                            );
                          })}
                          </div>
                        )}
                      </div>
                    );
                  })
                : groupedSeriesForCheckboxes.map(([groupKey, list]) => {
                    const groupTitle =
                      GROUP_TITLES[groupKey] || titleCaseGroup(groupKey);
                    const domKey = getToggleGroupDomKey("series", groupKey);
                    const panelId = `${domKey}-chart-toggle-panel`;
                    const selectedCount = (list || []).filter((s) =>
                      visibleKeys.includes(s.key),
                    ).length;
                    const totalCount = Array.isArray(list) ? list.length : 0;
                    const isOpen = openToggleGroupKey === domKey;

                    return (
                      <div key={groupKey} className="relative">
                        <button
                          type="button"
                          className="min-w-[180px] rounded border border-gray-300 bg-white px-4 py-3 text-left shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() =>
                            setOpenToggleGroupKey((prev) =>
                              prev === domKey ? null : domKey,
                            )
                          }
                        >
                          <span className="flex items-center justify-between gap-4">
                            <span>
                              <span className="block text-sm font-semibold text-gray-900">
                                {groupTitle}
                              </span>
                              <span className="block text-xs text-gray-600">
                                {selectedCount} of {totalCount} selected
                              </span>
                            </span>
                            <span
                              aria-hidden="true"
                              className="text-lg font-semibold text-gray-700"
                            >
                              {isOpen ? "^" : "v"}
                            </span>
                          </span>
                        </button>

                        {isOpen && (
                          <div
                            id={panelId}
                            className="absolute left-0 top-full z-50 mt-2 w-[min(360px,calc(100vw-2rem))] rounded border border-gray-300 bg-white p-2 shadow-lg"
                          >
                          {list.map((s) => {
                            const displayLabel = stripGroupPrefix(
                              s.label,
                              s.group,
                            );
                            const disabled = !!s.disabled;

                            return (
                              <label
                                key={s.key}
                                className={`flex items-start gap-3 rounded px-3 py-3 text-sm ${
                                  disabled
                                    ? "cursor-not-allowed text-gray-400"
                                    : "cursor-pointer text-gray-900 hover:bg-gray-50"
                                }`}
                                title={s.label}
                              >
                                <input
                                  type="checkbox"
                                  checked={visibleKeys.includes(s.key)}
                                  disabled={disabled}
                                  onChange={() => toggleKey(s.key)}
                                  className="mt-0.5 h-5 w-5 rounded border-gray-400"
                                />
                                <span className="leading-4">
                                  {displayLabel}
                                </span>
                              </label>
                            );
                          })}
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>
          )}
        </div>
      )}

      {/* ✅ ensure chart is below toggles in stacking context */}
      {showChartSummary && <ChartSummary data={trimmedChartData} />}
      <div className="p-3 w-full overflow-x-auto relative z-0">
        {chartType === "bar" ||
        chartType === "stackedBar" ||
        chartType === "stackedBar100" ? (
          <Bar data={trimmedChartData} options={options} />
        ) : (
          <Line data={trimmedChartData} options={options} />
        )}
      </div>
    </div>
  );
}

/**
 * Specialized 2-line comparison chart for:
 * - Budget (Approved or Actual)
 * - Inflation baseline
 *
 * This component intentionally does NOT include series toggles; it uses a small
 * radio selector to choose which budget line to show.
 *
 * @component
 * @param {Object} props
 * @param {string[]} props.labels - X-axis labels (e.g., fiscal years).
 * @param {(number[]|null[])} props.approvedData - Approved budget values aligned with labels.
 * @param {(number[]|null[])} props.actualData - Actual budget values aligned with labels.
 * @param {(number[]|null[])} props.inflationData - Inflation baseline values aligned with labels.
 * @param {string} [props.title] - Optional title displayed above the chart.
 * @param {"approved"|"actual"} [props.defaultMode="approved"] - Default budget line to display.
 * @returns {JSX.Element}
 */
export function BudgetInflationChart(props) {
  return <SharedGraph title={props.title} shareId={props.shareId}>{id => <SharedInflationChart key={id} {...props} shareId={id}/>}</SharedGraph>;
}
function SharedInflationChart({
  shareId,
  labels,
  approvedData,
  actualData,

  inflationData,
  inflationDataForActual,
  inflationDataForApproved,

  title,
  defaultMode = "approved",

  // ✅ NEW: allow callers to hide the Approved/Actual toggle
  showModeToggle = true,

  // ✅ NEW: customize the budget line labels
  approvedLabel = "Approved Budget",
  actualLabel = "Actual Spending",
}) {
  const normalizedDefaultMode =
    defaultMode === "actual" ? "actual" : "approved";

  // If the toggle is hidden, mode is effectively pinned.
  const [mode, setMode] = useState(() => ['approved','actual'].includes(readSharedReport(shareId).mode) ? readSharedReport(shareId).mode : normalizedDefaultMode);
  const activateShare = useSharedReport(shareId, undefined, {mode});

  useEffect(() => {
    if (!showModeToggle) {
      setMode(normalizedDefaultMode);
    }
  }, [showModeToggle, normalizedDefaultMode]);

  const chartData = useMemo(() => {
    const labelsRaw = Array.isArray(labels) ? labels : [];
    const approvedRaw = Array.isArray(approvedData) ? approvedData : [];
    const actualRaw = Array.isArray(actualData) ? actualData : [];

    const budgetDataRaw = mode === "approved" ? approvedRaw : actualRaw;

    const inflRaw =
      mode === "approved"
        ? inflationDataForApproved || inflationData || []
        : inflationDataForActual || inflationData || [];

    const isPresent = (v) => typeof v === "number" && Number.isFinite(v);

    let start = 0;
    while (start < labelsRaw.length && !isPresent(budgetDataRaw[start])) {
      start += 1;
    }

    let end = labelsRaw.length - 1;
    while (end >= 0 && !isPresent(budgetDataRaw[end])) {
      end -= 1;
    }

    const budgetLineLabel = mode === "approved" ? approvedLabel : actualLabel;

    const empty = {
      labels: [],
      datasets: [
        {
          label: budgetLineLabel,
          data: [],
          fill: false,
          tension: 0.25,
          borderWidth: 3,
          borderColor: mode === "approved" ? "#2563eb" : "#16a34a",
          backgroundColor: mode === "approved" ? "#2563eb" : "#16a34a",
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHitRadius: 8,
        },
        {
          label: "Inflation Baseline",
          data: [],
          fill: false,
          tension: 0.25,
          borderWidth: 3,
          borderColor: "#f97316",
          backgroundColor: "#f97316",
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHitRadius: 8,
        },
      ],
    };

    if (start >= labelsRaw.length || end < start) return empty;

    const filteredLabels = labelsRaw.slice(start, end + 1);
    const budgetData = budgetDataRaw.slice(start, end + 1);
    const infl = (Array.isArray(inflRaw) ? inflRaw : []).slice(start, end + 1);

    // Anchor baseline to first visible budget point
    if (isPresent(budgetData[0])) {
      const b0 = budgetData[0];
      const i0 = infl[0];

      if (isPresent(i0)) {
        const scale = i0 !== 0 ? b0 / i0 : 1;
        if (Number.isFinite(scale) && scale !== 1) {
          for (let i = 0; i < infl.length; i++) {
            if (isPresent(infl[i])) infl[i] = infl[i] * scale;
          }
        }
      } else {
        infl[0] = b0;
      }
    }

    return {
      labels: filteredLabels,
      datasets: [
        {
          label: budgetLineLabel,
          data: budgetData,
          fill: false,
          tension: 0.25,
          borderWidth: 3,
          borderColor: mode === "approved" ? "#2563eb" : "#16a34a",
          backgroundColor: mode === "approved" ? "#2563eb" : "#16a34a",
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHitRadius: 8,
        },
        {
          label: "Inflation Baseline",
          data: infl,
          fill: false,
          tension: 0.25,
          borderWidth: 3,
          borderColor: "#f97316",
          backgroundColor: "#f97316",
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHitRadius: 8,
        },
      ],
    };
  }, [
    labels,
    approvedData,
    actualData,
    inflationData,
    inflationDataForActual,
    inflationDataForApproved,
    mode,
    approvedLabel,
    actualLabel,
  ]);

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: (ctx) => `$${Number(ctx.raw || 0).toLocaleString("en-US")}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (v) => `$${Number(v).toLocaleString("en-US")}`,
        },
        title: { display: true, text: "Budget / Inflation Baseline" },
      },
      x: { title: { display: true, text: "Fiscal Year" } },
    },
  };

  return (
    <div onChange={activateShare} onClick={activateShare} className="mt-4 border border-gray-300 rounded bg-white">
      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        {title ? <h3 className="text-lg font-semibold">{title}</h3> : <div />}

        {showModeToggle ? (
          <div className="flex items-center gap-4 text-xs">
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="budgetMode"
                value="approved"
                checked={mode === "approved"}
                onChange={() => setMode("approved")}
                className="border-gray-400"
              />
              <span>{approvedLabel}</span>
            </label>

            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="radio"
                name="budgetMode"
                value="actual"
                checked={mode === "actual"}
                onChange={() => setMode("actual")}
                className="border-gray-400"
              />
              <span>{actualLabel}</span>
            </label>
          </div>
        ) : (
          <div />
        )}
      </div>

      <div className="p-4 w-full overflow-x-auto">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
}
